import React from 'react';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Switch } from 'antd';
import { useTheme } from 'styled-components';
import {
  StyledSessionPanel,
  StyledSessionPanelHeader,
  StyledHeaderButton,
} from './index.styled';
import { LogoRenderer } from './LogoRenderer';
import type { LogoKey } from './LogoRenderer';

type LogoOption = {
  key: LogoKey;
  name: string;
  sub: string;
};

const LOGO_OPTIONS: LogoOption[] = [
  { key: 'default', name: 'Default', sub: 'Original Logo' },
  { key: 'gradient-orb', name: 'Gradient Orb', sub: 'Indigo + Cyan' },
  { key: 'mesh-blob', name: 'Mesh Blob', sub: 'Pink + Purple' },
  { key: 'glass-ring', name: 'Glass Ring', sub: 'Sky Blue' },
  { key: 'starburst', name: 'Starburst', sub: 'Amber + Gold' },
  { key: 'glass-panel', name: 'Glass Panel', sub: 'Frosted + White' },
  { key: 'aurora', name: 'Aurora', sub: 'Teal + Indigo' },
  { key: 'dot-matrix', name: 'Dot Matrix', sub: 'Purple + Neural' },
  { key: 'saturn', name: 'Saturn', sub: 'Violet + Deep' },
];

type Props = {
  isOpen: boolean;
  selected: LogoKey;
  usePrimaryColor: boolean;
  onSelect: (key: LogoKey) => void;
  onTogglePrimaryColor: (value: boolean) => void;
  onClose: () => void;
};

const LogoSettings: React.FC<Props> = ({ isOpen, selected, usePrimaryColor, onSelect, onTogglePrimaryColor, onClose }) => {
  const theme = useTheme() as any;

  return (
    <StyledSessionPanel $isOpen={isOpen}>
      <StyledSessionPanelHeader>
        <span>Logo Preview</span>
        <StyledHeaderButton
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label="Close settings"
        />
      </StyledSessionPanelHeader>
      <div style={{ padding: '12px 16px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: theme.palette.text.secondary }}>Use primary color</span>
        <Switch size="small" checked={usePrimaryColor} onChange={onTogglePrimaryColor} />
      </div>
      <div style={{
        flex: 1, overflowY: 'auto', padding: 16,
        display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12,
      }}>
        {LOGO_OPTIONS.map((logo) => {
          const isSelected = selected === logo.key;
          return (
            <div
              key={logo.key}
              onClick={() => onSelect(logo.key)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                padding: '16px 8px 12px', borderRadius: 12, cursor: 'pointer',
                border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.borderColor}`,
                background: isSelected ? `${theme.palette.primary.main}0A` : theme.palette.background.default,
                transition: 'border-color 0.2s, background 0.2s',
                position: 'relative',
              }}
            >
              {isSelected && (
                <div style={{
                  position: 'absolute', top: 6, right: 6,
                  width: 18, height: 18, borderRadius: '50%',
                  background: theme.palette.primary.main,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CheckOutlined style={{ color: '#fff', fontSize: 10 }} />
                </div>
              )}
              <div style={{
                width: 56, height: 56,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <LogoRenderer logoKey={logo.key} size={44} usePrimaryColor={usePrimaryColor} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.palette.text.primary }}>
                  {logo.name}
                </div>
                <div style={{ fontSize: 10, color: theme.palette.text.secondary, marginTop: 2 }}>
                  {logo.sub}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </StyledSessionPanel>
  );
};

export default LogoSettings;
